import { useState, useMemo } from "react";
import { XIcon } from "lucide-react";

import { LoadboardBookingModal, LoadboardBidModal, LoadboardCMRateInfo, LoadboardCancelModal } from "@/components/dataPages/loadboard";
import { TranslatedText } from "@/components/common/text";
import { getNumberWithCommas } from "@/utils/text";
import { useSelector } from "react-redux";
import { cn } from "@/utils/css";
import { useIsBroker } from "@/auth/roles";

export const LoadboardBidCard = ({ load, CMRate, isOpen, counterPanel }) => {
    const isBroker = useIsBroker();
    const [openBidInput, setOpenBidInput] = useState(false);
    const [bidAmount, setBidAmount] = useState("");
    const [openQuoteModal, setOpenQuoteModal] = useState(false);
    const [openBookingModal, setOpenBookingModal] = useState(false);
    const [openCancelModal, setOpenCancelModal] = useState(false);
    const { trailerType } = useSelector((state) => state.currentSearch);
    const { quotes, bookings } = useSelector((state) => state.offers);

    // Find if this load has an active quote or booking - only when row is open
    const activeOffer = useMemo(() => {
        if (!isOpen) return null;

        // Check quotes
        const quote = quotes.data?.find((q) => q.loadBoardItem.uniqueId === load.uniqueId);
        if (quote && ["ACCEPTED", "PENDING", "NOT_SUBMITTED", "UNKNOWN", "DECLINED"].includes(quote.status)) {
            return { ...quote, type: "quote" };
        }

        // Check bookings
        const booking = bookings.data?.find((b) => b.loadBoardItem.uniqueId === load.uniqueId);

        if (booking && ["ACCEPTED", "PENDING", "NOT_SUBMITTED", "UNKNOWN", "DECLINED"].includes(booking.status)) {
            return { ...booking, type: "booking" };
        }

        return null;
    }, [isOpen, quotes.data, bookings.data, load.uniqueId]);

    const quoteEnabled = load.allowedActions.includes("QUOTE");
    const bookEnabled = load.allowedActions.includes("BOOK");
    const cancelEnabled = load.allowedActions.includes("CANCEL");

    const handleBidAmountChange = (e) => {
        const value = e.target.value;
        // Remove existing commas for validation
        const rawValue = value.replace(/,/g, "");

        // Allow only numbers and a single decimal point
        if (/^\d*\.?\d*$/.test(rawValue)) {
            // Format with commas for display
            if (rawValue === "" || rawValue === ".") {
                setBidAmount(rawValue);
            } else {
                const parts = rawValue.split(".");
                const integerPart = parts[0];
                const decimalPart = parts[1];

                // Format integer part with commas
                const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

                // Reconstruct the value
                const formattedValue = decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;

                setBidAmount(formattedValue);
            }
        }
    };

    if (!isOpen || isBroker) {
        return (
            <div className="w-full lg:w-[23.897582%] flex flex-col h-[321px] bg-cm-p-surface rounded-lg p-4">
                <LoadboardCMRateInfo load={load} CMRate={CMRate} trailerType={trailerType} />
                {counterPanel}
            </div>
        );
    }

    return (
        <div className="w-full lg:w-[23.897582%] flex flex-col h-[321px] bg-cm-p-surface rounded-lg p-4">
            <LoadboardCMRateInfo load={load} CMRate={CMRate} trailerType={trailerType} />
            {counterPanel ? <div className="flex-1 flex flex-col justify-end">{counterPanel}</div> : null}
            {!counterPanel && (
                <div className="flex-1 flex flex-col gap-3 justify-end">
                    {activeOffer ? (
                        <>
                            <div
                                className={cn("w-full text-sm/[22px] text-cm-p-text-primary p-3 bg-cm-p-surface-container rounded-lg", {
                                    "bg-cm-p-container-green": activeOffer.status === "ACCEPTED",
                                })}
                            >
                                <div className="w-full">
                                    <TranslatedText>Your</TranslatedText>{" "}
                                    <TranslatedText>{activeOffer.type === "quote" ? "bid of" : "booking for"}</TranslatedText>{" "}
                                    <span className="font-medium">${getNumberWithCommas(activeOffer.offerRate, true)}</span>{" "}
                                    {activeOffer.status === "ACCEPTED" ? (
                                        <TranslatedText>has been accepted by the broker.</TranslatedText>
                                    ) : null}
                                    {activeOffer.status === "PENDING" || activeOffer.status === "NOT_SUBMITTED" ? (
                                        <TranslatedText>has been submitted and is being processed.</TranslatedText>
                                    ) : null}
                                    {activeOffer.status === "DECLINED" ? <TranslatedText>has been declined.</TranslatedText> : null}
                                    {activeOffer.status === "UNKNOWN" ? (
                                        <TranslatedText>is closed. The broker doesn't share whether it was accepted.</TranslatedText>
                                    ) : null}
                                </div>
                            </div>
                            {cancelEnabled &&
                            (activeOffer.status === "PENDING" ||
                                activeOffer.status === "NOT_SUBMITTED" ||
                                (activeOffer.status === "DECLINED" && activeOffer.type === "quote")) ? (
                                <LoadboardCancelModal
                                    offer={activeOffer}
                                    open={openCancelModal}
                                    onOpenChange={setOpenCancelModal}
                                    isDisabled={false}
                                    reasonType="loadboard"
                                    buttonClassName="bg-cm-p-on-container-red h-11 rounded-lg w-full inline-flex items-center justify-center text-cm-p-surface-hover"
                                />
                            ) : null}
                        </>
                    ) : (
                        // Show bid input and book button when no active offer
                        <>
                            {!quoteEnabled ? (
                                <div className="text-cm-p-text-secondary text-xs/[19px] text-center w-full">
                                    <TranslatedText>{`Broker did not provide option to ${bookEnabled ? "bid" : "book/bid"} this load`}</TranslatedText>
                                </div>
                            ) : openBidInput ? (
                                <div className="w-full flex flex-row gap-[10px]">
                                    <div className="w-[calc(100%-54px)] border border-cm-p-accent rounded-lg flex flex-row items-center overflow-hidden">
                                        <div className="w-[calc(100%-105px)] flex flex-row items-center justify-center">
                                            <div className="flex font-medium text-sm/[19px] w-1/2 pl-[calc(50%-12px)] pr-2">$</div>
                                            <div className="w-1/2 min-w-[42px]">
                                                <input
                                                    type="text"
                                                    className="min-w-[38px] w-full border-0 focus:border-0 ring-0 focus:ring-0 bg-transparent focus:outline-none p-0 caret-cm-p-accent placeholder-cm-p-text-secondary"
                                                    onChange={handleBidAmountChange}
                                                    placeholder={getNumberWithCommas(load.offerRate, true) || 0}
                                                    value={bidAmount}
                                                />
                                            </div>
                                        </div>
                                        <LoadboardBidModal
                                            load={load}
                                            bidAmount={bidAmount}
                                            open={openQuoteModal}
                                            onOpenChange={setOpenQuoteModal}
                                            isDisabled={!bidAmount}
                                            onConfirmBid={() => setOpenBidInput(false)}
                                        />
                                    </div>
                                    <button
                                        className="w-11 h-11 bg-cm-p-surface-container rounded-lg inline-flex justify-center items-center"
                                        onClick={() => setOpenBidInput(false)}
                                    >
                                        <XIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setOpenBidInput(true)}
                                    disabled={!quoteEnabled || load.isUnavailable}
                                    className="bg-cm-p-surface-container text-cm-p-text-primary w-full h-11 rounded-lg font-medium disabled:opacity-30"
                                >
                                    <TranslatedText text="Make a bid..." />
                                </button>
                            )}
                            {!bookEnabled ? (
                                quoteEnabled ? (
                                    <div className="text-cm-p-text-secondary text-xs/[19px] text-center w-full">
                                        <TranslatedText>Broker did not provide option to book this load</TranslatedText>
                                    </div>
                                ) : null
                            ) : (
                                <LoadboardBookingModal
                                    load={load}
                                    open={openBookingModal}
                                    onOpenChange={setOpenBookingModal}
                                    isDisabled={!bookEnabled || load.isUnavailable || openBidInput}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
